/* Alpha Grande — theme core. Vanilla web components, no framework.
   Every component is self-contained; nothing runs unless its element exists. */

(() => {
  'use strict';

  const EASE_MS = 250;

  /* ------------------------------------------------------------------
     Utilities
  ------------------------------------------------------------------ */
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const template = format || window.AlphaTheme?.moneyFormat || '${{amount}}';
    return template
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimal}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas);
  };

  const trapFocus = (container) => {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const nodes = [...container.querySelectorAll(selector)].filter((el) => el.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeydown);
    return () => container.removeEventListener('keydown', onKeydown);
  };

  const lockScroll = (lock) => {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  };

  const priceTick = (el) => {
    if (!el) return;
    el.classList.remove('price-ticked');
    void el.offsetWidth; /* restart animation */
    el.classList.add('price-ticked');
  };

  /* ------------------------------------------------------------------
     <cutoff-clock> — ONE live countdown, minute-granular, single truth.
     Attributes:
       data-cutoff="16:00"           shop-local cutoff (24h)
       data-timezone="America/Chicago"
       data-days="1,2,3,4,5"         ISO weekdays with same-day production
       data-template="Order by {cutoff} — {remaining} left today"
       data-after="Today's cutoff passed — orders join tomorrow's run."
       data-offday="Weekend orders join Monday's production run."
  ------------------------------------------------------------------ */
  class CutoffClock extends HTMLElement {
    connectedCallback() {
      this.render();
      /* minute-granular: never tick seconds */
      this.timer = setInterval(() => this.render(), 30 * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    now() {
      const tz = this.dataset.timezone || 'America/Chicago';
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        weekday: 'short', hour: 'numeric', minute: 'numeric',
      }).formatToParts(new Date());
      const get = (type) => parts.find((p) => p.type === type)?.value;
      const isoDay = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }[get('weekday')];
      return { day: isoDay, hour: parseInt(get('hour'), 10) % 24, minute: parseInt(get('minute'), 10) };
    }

    render() {
      const { day, hour, minute } = this.now();
      const days = (this.dataset.days || '1,2,3,4,5').split(',').map(Number);
      const [cutH, cutM] = (this.dataset.cutoff || '16:00').split(':').map(Number);
      const remaining = (cutH * 60 + cutM) - (hour * 60 + minute);

      let text;
      if (!days.includes(day)) {
        text = this.dataset.offday || '';
      } else if (remaining <= 0) {
        text = this.dataset.after || '';
      } else {
        const h = Math.floor(remaining / 60);
        const m = remaining % 60;
        const span = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
        text = (this.dataset.template || '{remaining}')
          .replace('{remaining}', span)
          .replace('{cutoff}', this.dataset.cutoffLabel || '4:00 PM CT');
        this.classList.toggle('is-closing', remaining <= 60);
      }
      if (text !== this.lastText) {
        this.innerHTML = text.replace(/(\d+h \d+m|\d+m)(?= left| →|$)/, '<strong class="accent tabular">$1</strong>');
        this.lastText = text;
      }
      this.toggleAttribute('data-past-cutoff', days.includes(day) && remaining <= 0);
    }
  }
  customElements.define('cutoff-clock', CutoffClock);

  /* ------------------------------------------------------------------
     <quantity-input> — stepper with press-and-hold repeat
  ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        const step = button.dataset.step === 'down' ? -1 : 1;
        let holdTimeout;
        let holdInterval;
        const change = () => this.change(step);
        button.addEventListener('click', change);
        button.addEventListener('pointerdown', () => {
          holdTimeout = setTimeout(() => { holdInterval = setInterval(change, 80); }, 400);
        });
        ['pointerup', 'pointerleave', 'pointercancel'].forEach((type) =>
          button.addEventListener(type, () => { clearTimeout(holdTimeout); clearInterval(holdInterval); }));
      });
      this.input?.addEventListener('change', () => this.emit());
    }

    change(step) {
      if (!this.input) return;
      const min = parseInt(this.input.min || '1', 10);
      const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
      this.input.value = next;
      this.emit();
    }

    emit() {
      this.dispatchEvent(new CustomEvent('quantity:change', {
        bubbles: true,
        detail: { value: parseInt(this.input.value, 10) || 1 },
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------
     <variant-picker> — option cells → variant resolution → price tick.
     Expects a sibling <script type="application/json" data-variants>.
  ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.section = this.closest('[data-product-root]') || document;
      this.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"], select')) return;
        /* one option, two controls (cells + mobile dropdown) — keep in sync */
        const group = event.target.closest('[data-option-index]');
        if (group) {
          const value = event.target.value;
          group.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.checked = radio.value === value;
          });
          const select = group.querySelector('select');
          if (select) select.value = value;
        }
        this.update();
      });
    }

    selectedOptions() {
      const options = [];
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        const checked = group.querySelector('input:checked') || group.querySelector('select');
        options[index] = checked ? checked.value : null;
      });
      return options;
    }

    update() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) =>
        v.options.every((value, i) => value === options[i]));

      /* reflect selected state on cells */
      this.querySelectorAll('.opt-cell').forEach((cell) => {
        const input = cell.querySelector('input');
        cell.classList.toggle('is-selected', !!input?.checked);
      });

      const idInput = this.section.querySelector('input[name="id"]');
      const priceEl = this.section.querySelector('[data-price]');
      const compareEl = this.section.querySelector('[data-compare-price]');
      const buttons = this.section.querySelectorAll('[data-atc]');
      const stickyPrice = this.section.querySelector('[data-sticky-price]');

      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; b.querySelector('span') && (b.querySelector('span').textContent = b.dataset.unavailableLabel || 'Unavailable'); });
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) { priceEl.textContent = formatMoney(variant.price); priceTick(priceEl); }
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (compareEl) {
        const onSale = variant.compare_at_price > variant.price;
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price);
      }
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        const label = b.querySelector('span');
        if (label) label.textContent = variant.available ? (b.dataset.addLabel || label.textContent) : (b.dataset.soldOutLabel || 'Sold out');
      });

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------
     <tier-table> — highlights the row matching the live quantity and
     writes the "add N more" nudge. Rows carry data-min / data-max.
  ------------------------------------------------------------------ */
  class TierTable extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      this.nudge = this.querySelector('[data-tier-nudge]');
      this.root.addEventListener('quantity:change', (event) => this.update(event.detail.value));
      this.update(1);
    }

    update(qty) {
      let nextBreak = null;
      this.querySelectorAll('tr[data-min]').forEach((row) => {
        const min = parseInt(row.dataset.min, 10);
        const max = row.dataset.max ? parseInt(row.dataset.max, 10) : Infinity;
        const active = qty >= min && qty <= max;
        row.classList.toggle('is-active', active);
        if (min > qty && (nextBreak === null || min < nextBreak.min)) {
          nextBreak = { min, label: row.dataset.label || '' };
        }
      });
      if (this.nudge) {
        if (nextBreak && nextBreak.label) {
          const more = nextBreak.min - qty;
          this.nudge.textContent = this.nudge.dataset.template
            .replace('{count}', more)
            .replace('{price}', nextBreak.label);
          this.nudge.hidden = false;
        } else {
          this.nudge.hidden = true;
        }
      }
    }
  }
  customElements.define('tier-table', TierTable);

  /* ------------------------------------------------------------------
     <product-gallery> — thumbnails crossfade the stage. No slide.
  ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage] img');
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-thumb]');
        if (!thumb || !this.stage) return;
        event.preventDefault();
        this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-selected'));
        thumb.classList.add('is-selected');
        this.stage.style.opacity = '0';
        setTimeout(() => {
          this.stage.src = thumb.dataset.src;
          this.stage.srcset = thumb.dataset.srcset || '';
          this.stage.alt = thumb.dataset.alt || '';
          this.stage.onload = () => { this.stage.style.opacity = '1'; };
        }, 150);
      });

      this.addEventListener('variant:change', (event) => {
        const image = event.detail.variant?.featured_image;
        if (!image) return;
        const thumb = this.querySelector(`[data-media-id="${image.id}"]`);
        if (thumb) thumb.click();
      });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------
     <sticky-atc> — mobile bottom bar / desktop slim bar. Appears only
     after the real buy box scrolls off.
  ------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchorSelector = this.dataset.anchor || '[data-buy-box]';
      const anchor = document.querySelector(anchorSelector);
      if (!anchor) return;
      const observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      observer.observe(anchor);

      this.querySelector('[data-sticky-trigger]')?.addEventListener('click', () => {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------
     Cart API + <cart-drawer>
     Add-to-cart forms are intercepted; the drawer re-renders itself via
     the Section Rendering API and opens. Production-expectation line and
     free-shipping meter live inside the section markup.
  ------------------------------------------------------------------ */
  const Cart = {
    async add(formData) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Could not add to cart.');
      }
      return response.json();
    },

    async change(line, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      return response.json();
    },
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.drawer-overlay');
      this.panel = this.querySelector('.drawer');
      this.bindInner();

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open]');
        if (opener) { event.preventDefault(); this.open(); }
      });
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panel?.classList.contains('is-open')) this.close();
      });

      /* Intercept product forms */
      document.addEventListener('submit', async (event) => {
        const form = event.target.closest('form[action*="/cart/add"]');
        if (!form || form.hasAttribute('data-native-submit')) return;
        event.preventDefault();
        const button = form.querySelector('[data-atc]') || form.querySelector('[type="submit"]');
        button?.classList.add('is-loading');
        if (button) button.disabled = true;
        try {
          await Cart.add(new FormData(form));
          await this.refresh();
          this.open();
        } catch (error) {
          this.announce(error.message, form);
        } finally {
          button?.classList.remove('is-loading');
          if (button) button.disabled = false;
        }
      });
    }

    bindInner() {
      this.querySelectorAll('[data-line-change]').forEach((el) => {
        el.addEventListener('quantity:change', async (event) => {
          const line = parseInt(el.dataset.lineChange, 10);
          this.panel?.classList.add('is-updating');
          await Cart.change(line, event.detail.value);
          await this.refresh();
        });
      });
      this.querySelectorAll('[data-line-remove]').forEach((el) => {
        el.addEventListener('click', async (event) => {
          event.preventDefault();
          this.panel?.classList.add('is-updating');
          await Cart.change(parseInt(el.dataset.lineRemove, 10), 0);
          await this.refresh();
        });
      });
    }

    async refresh() {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const sections = await response.json();
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = html.querySelector('cart-drawer');
      if (fresh) {
        const wasOpen = this.panel?.classList.contains('is-open');
        this.querySelector('.drawer__inner-wrap').innerHTML = fresh.querySelector('.drawer__inner-wrap').innerHTML;
        this.overlay = this.querySelector('.drawer-overlay');
        this.panel = this.querySelector('.drawer');
        if (wasOpen) {
          this.overlay?.classList.add('is-open');
          this.panel?.classList.add('is-open');
        }
        this.overlay?.addEventListener('click', () => this.close());
        this.bindInner();
      }
      document.querySelectorAll('[data-cart-count]').forEach(async (badge) => {
        const cart = await (await fetch('/cart.js')).json();
        badge.textContent = cart.item_count;
        badge.toggleAttribute('hidden', cart.item_count === 0);
      });
    }

    open() {
      this.overlay?.classList.add('is-open');
      this.panel?.classList.add('is-open');
      lockScroll(true);
      this.releaseFocus = trapFocus(this.panel);
      this.panel?.querySelector('.drawer__close')?.focus();
    }

    close() {
      this.overlay?.classList.remove('is-open');
      this.panel?.classList.remove('is-open');
      lockScroll(false);
      this.releaseFocus?.();
    }

    announce(message, form) {
      let region = form.querySelector('[data-form-error]');
      if (region) {
        region.textContent = message;
        region.hidden = false;
        setTimeout(() => { region.hidden = true; }, 5000);
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ------------------------------------------------------------------
     <menu-drawer> — mobile navigation
  ------------------------------------------------------------------ */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.drawer-overlay');
      this.panel = this.querySelector('.drawer');
      document.querySelectorAll('[data-menu-open]').forEach((btn) =>
        btn.addEventListener('click', () => this.open()));
      this.querySelector('.drawer__close')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panel?.classList.contains('is-open')) this.close();
      });
    }
    open() {
      this.overlay?.classList.add('is-open');
      this.panel?.classList.add('is-open');
      lockScroll(true);
      this.releaseFocus = trapFocus(this.panel);
    }
    close() {
      this.overlay?.classList.remove('is-open');
      this.panel?.classList.remove('is-open');
      lockScroll(false);
      this.releaseFocus?.();
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ------------------------------------------------------------------
     Header condense — 72 → 60px after 24px scroll. The only glass.
  ------------------------------------------------------------------ */
  const header = document.querySelector('[data-header]');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-condensed', window.scrollY > 24);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     <predictive-search> — quiet, fast, keyboard-friendly
  ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;
      let debounce;
      this.input.addEventListener('input', () => {
        clearTimeout(debounce);
        const query = this.input.value.trim();
        if (query.length < 2) { this.results.hidden = true; return; }
        debounce = setTimeout(() => this.search(query), 220);
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.results.hidden = true;
      });
    }

    async search(query) {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
        const data = await (await fetch(url)).json();
        const products = data.resources.results.products || [];
        if (!products.length) { this.results.hidden = true; return; }
        this.results.innerHTML = products.map((p) => `
          <a href="${p.url}" class="search-hit">
            ${p.featured_image?.url ? `<img src="${p.featured_image.url}&width=64" width="32" height="32" alt="" loading="lazy">` : ''}
            <span class="search-hit__title">${p.title}</span>
            <span class="search-hit__price spec">${p.price ? formatMoney(Math.round(parseFloat(p.price) * 100)) : ''}</span>
          </a>`).join('');
        this.results.hidden = false;
      } catch { this.results.hidden = true; }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------
     Reveal on scroll — one soft entrance, content only, disabled for
     reduced motion. Elements opt in with data-reveal.
  ------------------------------------------------------------------ */
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const revealables = document.querySelectorAll('[data-reveal]');
    if (revealables.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      revealables.forEach((el) => io.observe(el));
    }
  }
})();
